// ============================================================================
// DOCUMENT REPOSITORY
// ARQUIVO: src/services/DocumentRepository.js
// RESPONSABILIDADE: CRUD de documentos jurídicos, notificações e histórico.
// Criado em: 17/05/2026 — split de LegalCore.jsx (Fase 2 auditoria arquitetural)
// ============================================================================

import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from '../shared/api/firebase/config';
import { FS } from '../shared/constants/firestorePaths';
import { FIREBASE_CONFIG } from "../config/Config";

const APP_CONTEXT = FIREBASE_CONFIG.projectId || "portalrda-f312d";

export const saveLegalDocument = async (docData, userEmail) => {
  try {
    const docsRef = collection(db, ...FS.documents(APP_CONTEXT));
    const rawRelatedEntities = Array.isArray(docData?.relatedEntities) ? docData.relatedEntities : [];
    const normalizedRelatedEntityObjects = Array.from(
      new Map(
        rawRelatedEntities
          .map((item) => {
            if (item && typeof item === "object") {
              const id = String(item?.id || "").trim();
              if (!id) return null;
              return {
                id,
                type: String(item?.type || "").trim() || null,
                name: String(item?.name || "").trim() || null,
              };
            }
            const id = String(item || "").trim();
            if (!id) return null;
            return { id, type: null, name: null };
          })
          .filter(Boolean)
          .map((item) => [String(item.id).trim(), item])
      ).values()
    );

    const relatedEntityIds = Array.from(
      new Set(
        [
          ...(Array.isArray(docData?.context?.relatedEntities) ? docData.context.relatedEntities : []),
          ...normalizedRelatedEntityObjects.map((item) => String(item?.id || "").trim()),
          String(docData?.clientId || "").trim(),
        ].filter(Boolean)
      )
    );

    const payload = {
      name: docData.name,
      size: docData.size,
      type: docData.type,
      classification: {
        category: docData.category || "OUTROS",
        confidentiality: docData.isSealed ? "SEGREDO_JUSTICA" : "PUBLICO",
        tags: docData.tags || [],
      },
      context: {
        ...(docData?.context && typeof docData.context === "object" ? docData.context : {}),
        relatedEntities: relatedEntityIds,
        primaryEntityId: docData.primaryEntityId || null,
        primaryEntityName: docData.primaryEntityName || "Não Vinculado",
        uploadedBy: userEmail || "Sistema",
      },
      status: docData.primaryEntityId ? "VINCULADO" : "TRIAGEM",
      createdAt: serverTimestamp(),
      version: 1,
    };

    if (docData.storageEngineData && typeof docData.storageEngineData === "object") {
      payload.storageEngineData = docData.storageEngineData;
      payload.storageEngine = docData.storageEngineData;
    }
    if (docData.fileUrl) payload.fileUrl = String(docData.fileUrl).trim();

    const legacyLinkedClient = String(docData?.linkedClient || "").trim();
    if (legacyLinkedClient) payload.linkedClient = legacyLinkedClient;

    const canonicalClientId = String(docData?.clientId || "").trim();
    if (canonicalClientId) payload.clientId = canonicalClientId;

    if (normalizedRelatedEntityObjects.length > 0) {
      payload.relatedEntities = normalizedRelatedEntityObjects;
    }

    const safeCadernoId = String(docData?.cadernoId || "").trim();
    const safeCadernoTitle = String(docData?.cadernoTitle || "").trim();
    if (safeCadernoId) {
      payload.cadernoId = safeCadernoId;
      payload.cadernoTitle = safeCadernoTitle || null;
    }

    const docRef = await addDoc(docsRef, payload);
    return { success: true, id: docRef?.id };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const linkDocumentToContext = async (docId, linkData) => {
  try {
    const docRef = doc(db, ...FS.documents(APP_CONTEXT), docId);
    await updateDoc(docRef, {
      ...linkData,
      status: "VINCULADO",
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const deleteLegalDocument = async (docId) => {
  try {
    const docRef = doc(db, ...FS.documents(APP_CONTEXT), docId);
    await deleteDoc(docRef);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const saveFileHubAttachment = async (batchId, attachmentData, userEmail) => {
  try {
    const ref = collection(db, ...FS.fileHubAttachments(APP_CONTEXT));
    const payload = {
      batchId: String(batchId || ""),
      order: Number(attachmentData?.order ?? 0),
      virtualName: String(attachmentData?.virtualName || ""),
      finalName: String(attachmentData?.finalName || ""),
      originalName: String(attachmentData?.originalName || ""),
      sizeBytes: Number(attachmentData?.sizeBytes ?? 0),
      context: {
        client: String(attachmentData?.context?.client || ""),
        type: String(attachmentData?.context?.type || ""),
        date: String(attachmentData?.context?.date || ""),
      },
      drive: {
        file_id: String(attachmentData?.drive?.file_id || ""),
        webViewLink: String(attachmentData?.drive?.webViewLink || ""),
        hash: String(attachmentData?.drive?.hash || ""),
        folder: String(attachmentData?.drive?.folder || ""),
      },
      uploadedBy: userEmail || "Sistema",
      createdAt: serverTimestamp(),
      version: 1,
    };
    await addDoc(ref, payload);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const sendSystemNotification = async (notificationData) => {
  try {
    const notifRef = collection(db, ...FS.notifications(APP_CONTEXT));
    await addDoc(notifRef, {
      type: notificationData.type || "INFO",
      title: notificationData.title,
      description: notificationData.description,
      read: false,
      createdAt: serverTimestamp(),
      target: "ADMIN",
    });
    return { success: true };
  } catch (error) {
    console.error("Erro ao notificar:", error);
    return { success: false, error: error.message };
  }
};

export const saveContractHistory = async (contractData) => {
  try {
    const historyRef = collection(db, ...FS.contractsHistory(APP_CONTEXT));
    await addDoc(historyRef, { ...contractData, createdAt: serverTimestamp() });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};
